import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Phone, Mail, CheckCircle, Shield, Heart, GraduationCap } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { DIVISIONS } from "../lib/divisions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { id: "veteran", label: "Veteran Disability Consulting", icon: Shield, division: DIVISIONS.veteran },
  { id: "insurance", label: "Insurance Solutions", icon: Heart, division: DIVISIONS.insurance },
  { id: "education", label: "Education Assistance", icon: GraduationCap, division: DIVISIONS.education },
  { id: "general", label: "General Inquiry", icon: Mail, division: DIVISIONS.veteran },
];

const ContactPage = () => {
  const [selectedService, setSelectedService] = useState("veteran");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const activeDivision = useMemo(
    () => services.find((s) => s.id === selectedService)?.division || DIVISIONS.veteran,
    [selectedService]
  );

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Route through existing consultation endpoint; encode service in serviceHistory field
      await axios.post(`${API}/consultation`, {
        ...formData,
        serviceHistory: services.find((s) => s.id === selectedService)?.label,
      });
      setStatus("success");
      setFormData({ fullName: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <SectionHeading
          eyebrow="Contact Us"
          title="How can we help?"
          subtitle="Choose the service you need help with and we'll route your message to the right team."
          align="center"
        />

        {/* Service selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
          {services.map((s) => {
            const Icon = s.icon;
            const active = selectedService === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSelectedService(s.id)}
                data-testid={`contact-service-${s.id}`}
                className={`p-4 border-2 text-left transition-all ${
                  active
                    ? "border-[#C89B3C] bg-[#0B2343] text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                }`}
              >
                <Icon className={`mb-2 ${active ? "text-[#C89B3C]" : "text-[#0B2343]"}`} size={22} />
                <span className="text-sm font-medium block leading-tight">{s.label}</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left: Contact info based on selection */}
          <motion.div
            key={selectedService}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-slate-50 p-8 md:p-10"
          >
            <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
            <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">
              {activeDivision.name}
            </h3>
            <p className="text-slate-600 mb-8">{activeDivision.tagline}</p>

            <div className="space-y-5">
              <a
                href={activeDivision.phoneHref}
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-[#0B2343] flex items-center justify-center group-hover:bg-[#C89B3C] transition-colors">
                  <Phone className="text-[#C89B3C] group-hover:text-white transition-colors" size={20} />
                </div>
                <div>
                  <span className="text-xs text-slate-500 uppercase tracking-wider block">Phone</span>
                  <span className="text-slate-900 font-medium">{activeDivision.phone}</span>
                </div>
              </a>
              <a
                href={`mailto:${activeDivision.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-[#0B2343] flex items-center justify-center group-hover:bg-[#C89B3C] transition-colors">
                  <Mail className="text-[#C89B3C] group-hover:text-white transition-colors" size={20} />
                </div>
                <div>
                  <span className="text-xs text-slate-500 uppercase tracking-wider block">Email</span>
                  <span className="text-slate-900 font-medium break-all">{activeDivision.email}</span>
                </div>
              </a>
            </div>
          </motion.div>

          {/* Right: Contact form */}
          <div className="bg-white p-8 md:p-10 border border-slate-200">
            {status === "success" ? (
              <div className="text-center py-16" data-testid="contact-success">
                <CheckCircle className="text-green-600 mx-auto mb-4" size={56} />
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">Message Sent</h3>
                <p className="text-slate-600">
                  We'll get back to you within 24–48 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Send us a message
                </h3>
                <FormField label="Full Name" required>
                  <input
                    type="text"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 focus:border-slate-900 outline-none"
                    data-testid="contact-fullname"
                  />
                </FormField>
                <FormField label="Email" required>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 focus:border-slate-900 outline-none"
                    data-testid="contact-emailinput"
                  />
                </FormField>
                <FormField label="Phone (Optional)">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 focus:border-slate-900 outline-none"
                    data-testid="contact-phoneinput"
                  />
                </FormField>
                <FormField label="Message" required>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 focus:border-slate-900 outline-none resize-none"
                    data-testid="contact-message"
                  />
                </FormField>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0B2343] text-white px-8 py-4 font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
                {status === "error" && (
                  <p className="text-red-500 text-sm">
                    Something went wrong. Please try again or call us directly.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && "*"}
    </label>
    {children}
  </div>
);

export default ContactPage;
